import React, { useCallback, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/apiConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { Button } from '../../../components/ui/Button';
import { Switch } from '../../../components/ui/Switch';
import { cn } from '../../../lib/utils';
import { DocumentTextIcon, InfoIcon, UploadCloudIcon, XIcon } from '../../../components/ui/Icons';

type ImportResult = {
  totalRows: number;
  created: number;
  updated: number;
  linkedParents: number;
  missingParents: number;
};

type ProgressState = {
  percent: number;
  processedRows: number;
  totalRows: number;
  batchIndex: number;
  batchCount: number;
};

const emptyProgress: ProgressState = {
  percent: 0,
  processedRows: 0,
  totalRows: 0,
  batchIndex: 0,
  batchCount: 0,
};

const ACCEPT = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isAcceptableFile(file: File): boolean {
  const n = file.name.toLowerCase();
  if (n.endsWith('.xls') || n.endsWith('.xlsx')) return true;
  const m = (file.type || '').toLowerCase();
  return m.includes('spreadsheet') || m.includes('excel');
}

async function runAccountsPlanImportStream(
  file: File,
  deleteExisting: boolean,
  onProgress: (p: ProgressState) => void,
): Promise<ImportResult> {
  const token = localStorage.getItem('access_token');
  const body = new FormData();
  body.append('file', file);
  body.append('deleteExisting', deleteExisting ? 'true' : 'false');

  const res = await fetch(`${API_BASE_URL}/acc-accounts/import-plan`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(err?.message || `Import failed (${res.status})`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body from server.');

  const decoder = new TextDecoder();
  let buffer = '';
  let lastResult: ImportResult | null = null;

  const handleLine = (trimmed: string) => {
    if (!trimmed) return;
    const msg = JSON.parse(trimmed) as Record<string, unknown>;
    if (msg.type === 'progress') {
      onProgress({
        percent: Number(msg.percent) || 0,
        processedRows: Number(msg.processedRows) || 0,
        totalRows: Number(msg.totalRows) || 0,
        batchIndex: Number(msg.batchIndex) || 0,
        batchCount: Number(msg.batchCount) || 0,
      });
    } else if (msg.type === 'done') {
      lastResult = {
        totalRows: Number(msg.totalRows) || 0,
        created: Number(msg.created) || 0,
        updated: Number(msg.updated) || 0,
        linkedParents: Number(msg.linkedParents) || 0,
        missingParents: Number(msg.missingParents) || 0,
      };
    } else if (msg.type === 'error') {
      throw new Error(typeof msg.message === 'string' ? msg.message : 'Import failed');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) handleLine(line.trim());
  }
  if (buffer.trim()) handleLine(buffer.trim());

  if (!lastResult) throw new Error('Import finished without a result from the server.');
  return lastResult;
}

const AccountsPlanImportPage: React.FC = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [deleteExisting, setDeleteExisting] = useState(true);

  const [uploadDragActive, setUploadDragActive] = useState(false);
  const uploadDragDepth = useRef(0);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = useCallback(() => {
    setFile(null);
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = '';
    setResult(null);
    setProgress(null);
  }, []);

  const assignFile = useCallback(
    (f: File | null) => {
      if (!f) return;
      if (isAcceptableFile(f)) {
        setFile(f);
        setResult(null);
        setProgress(null);
      } else {
        showError('Use an Excel file (.xls or .xlsx) exported as Accounts Plan.');
      }
    },
    [showError],
  );

  const onUploadDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current += 1;
    const typeList = Array.from(e.dataTransfer?.types ?? []);
    if (typeList.includes('Files')) setUploadDragActive(true);
  }, []);

  const onUploadDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    uploadDragDepth.current -= 1;
    if (uploadDragDepth.current <= 0) {
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
    }
  }, []);

  const onUploadDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onUploadDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      uploadDragDepth.current = 0;
      setUploadDragActive(false);
      const dropped = e.dataTransfer.files?.[0];
      assignFile(dropped ?? null);
    },
    [assignFile],
  );

  const openFilePicker = useCallback(() => {
    uploadFileInputRef.current?.click();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showError('Drop a file above or use Browse files to choose your Accounts Plan.');
      return;
    }
    setBusy(true);
    setResult(null);
    setProgress({ ...emptyProgress });
    try {
      const data = await runAccountsPlanImportStream(file, deleteExisting, (p) => setProgress(p));
      setResult(data);
      setProgress((prev) => (prev ? { ...prev, percent: 100, processedRows: data.totalRows, totalRows: data.totalRows } : null));
      showSuccess(`Import finished: ${data.created} created, ${data.updated} updated (${data.totalRows} rows).`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Import failed.';
      showError(msg);
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn('min-h-[60vh] w-full max-w-3xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8 py-8 space-y-10', colors.background)}>
      <header className={cn('relative overflow-hidden rounded-2xl border shadow-sm', colors.border, colors.surface)}>
        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" aria-hidden />
        <div className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-4 max-w-2xl">
              <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-1 ring-indigo-500/20')}>
                <DocumentTextIcon className="w-6 h-6" aria-hidden />
              </div>
              <div>
                <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Administration · Import</p>
                <h1 className={cn('mt-1 text-2xl sm:text-3xl font-bold tracking-tight', colors.text)}>Accounts plan import</h1>
                <p className={cn('mt-2 text-sm leading-relaxed', colors.textSecondary)}>
                  Upload an Excel <code className="text-xs">.xls</code> / <code className="text-xs">.xlsx</code> with columns{' '}
                  <strong className={cn('font-medium', colors.text)}>level1_code</strong>,{' '}
                  <strong className={cn('font-medium', colors.text)}>level2_code</strong>,{' '}
                  <strong className={cn('font-medium', colors.text)}>level3_code</strong>,{' '}
                  <strong className={cn('font-medium', colors.text)}>level4_code</strong>, and{' '}
                  <strong className={cn('font-medium', colors.text)}>level_name</strong>. Each row creates one tree node; parents are linked by level depth (L4 = posting accounts).
                </p>
                <ol className={cn('mt-4 grid gap-2 text-xs sm:text-sm sm:grid-cols-2', colors.textSecondary)}>
                  {['1 · Upload file', '2 · Run import'].map((step) => (
                    <li key={step} className={cn('rounded-lg px-3 py-2 border', colors.border, colors.surfaceHover)}>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </header>

      <form onSubmit={onSubmit} className="space-y-0">
        <section aria-labelledby="accountsplan-upload-heading">
          <h2 id="accountsplan-upload-heading" className="sr-only">
            Upload Accounts Plan
          </h2>

          <input
            ref={uploadFileInputRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            tabIndex={-1}
            onChange={(e) => {
              const f = e.target.files?.[0];
              assignFile(f ?? null);
              e.target.value = '';
            }}
          />

          <div className={cn('rounded-2xl border shadow-md overflow-hidden', colors.border, colors.surface)}>
            <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
              <div className="flex items-start gap-3">
                <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400')}>
                  <UploadCloudIcon className="w-5 h-5" aria-hidden />
                </div>
                <div>
                  <p className={cn('text-base font-semibold', colors.text)}>Import file</p>
                  <p className={cn('text-sm mt-0.5 max-w-xl', colors.textSecondary)}>
                    Drop your workbook below or browse. The import is safe to re-run (updates existing accounts by code).
                  </p>
                </div>
              </div>
            </div>
            <div className={cn('px-5 py-4 border-b', colors.border, colors.surfaceHover)}>
              <Switch
                checked={deleteExisting}
                disabled={busy}
                onChange={(e) => setDeleteExisting(e.target.checked)}
                label="Delete existing accounts before import"
                description="Recommended when switching to combLevel-based codes. This will remove the whole chart of accounts."
                labelPosition="right"
              />
            </div>

            <div
              role="button"
              tabIndex={0}
              aria-label="Drop zone for Accounts Plan Excel file"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (!busy) openFilePicker();
                }
              }}
              onDragEnter={onUploadDragEnter}
              onDragLeave={onUploadDragLeave}
              onDragOver={onUploadDragOver}
              onDrop={onUploadDrop}
              onClick={(e) => {
                if (busy) return;
                if ((e.target as HTMLElement).closest('[data-no-dropzone-click]')) return;
                openFilePicker();
              }}
              className={cn(
                'relative min-h-[220px] px-6 py-10 cursor-pointer transition-all duration-200',
                busy && 'pointer-events-none opacity-60',
                'bg-gradient-to-b from-transparent via-indigo-500/[0.02] to-indigo-500/[0.06]',
                'dark:via-indigo-400/[0.03] dark:to-indigo-400/[0.07]',
                uploadDragActive
                  ? 'ring-2 ring-inset ring-indigo-500 border-indigo-500/40 dark:ring-indigo-400 scale-[1.002]'
                  : cn('border-2 border-dashed border-transparent', 'hover:border-indigo-400/50 dark:hover:border-indigo-500/40'),
              )}
            >
              <div className="pointer-events-none absolute inset-0 rounded-[inherit] border-2 border-dashed border-indigo-300/60 dark:border-indigo-500/35" />

              <div className="relative flex flex-col items-center justify-center text-center gap-3 max-w-lg mx-auto">
                <div
                  className={cn(
                    'flex h-16 w-16 items-center justify-center rounded-2xl transition-transform duration-200',
                    uploadDragActive ? 'bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 scale-110' : 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
                  )}
                >
                  <UploadCloudIcon className="w-9 h-9" aria-hidden />
                </div>
                <div>
                  <p className={cn('text-lg font-semibold', colors.text)}>{uploadDragActive ? 'Release to select file' : 'Drop Accounts Plan file here'}</p>
                  <p className={cn('text-sm mt-1', colors.textSecondary)}>
                    <button
                      type="button"
                      data-no-dropzone-click
                      onClick={(ev) => {
                        ev.stopPropagation();
                        if (!busy) openFilePicker();
                      }}
                      className={cn('font-semibold underline decoration-indigo-500/60 underline-offset-2 hover:decoration-indigo-600', colors.primaryFg)}
                    >
                      Browse files
                    </button>
                    <span className="mx-1.5 opacity-50" aria-hidden>
                      ·
                    </span>
                    <span>.xls, .xlsx</span>
                  </p>
                </div>
              </div>
            </div>

            {busy && progress && progress.totalRows > 0 && (
              <div className={cn('px-5 py-4 border-t space-y-2', colors.border, colors.surfaceHover)} aria-live="polite">
                <div className={cn('h-2.5 w-full overflow-hidden rounded-full', colors.border)} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent} aria-label="Import progress">
                  <div className="h-full rounded-full bg-indigo-600 transition-[width] duration-200 ease-out dark:bg-indigo-500" style={{ width: `${progress.percent}%` }} />
                </div>
                <p className={cn('text-sm tabular-nums', colors.textSecondary)}>
                  {progress.percent}% — processed {progress.processedRows.toLocaleString()} / {progress.totalRows.toLocaleString()}
                  {progress.batchIndex > 0 && progress.batchCount > 0 && (
                    <>
                      {' '}
                      (batch {progress.batchIndex} / {progress.batchCount})
                    </>
                  )}
                </p>
              </div>
            )}

            {busy && (!progress || progress.totalRows === 0) && (
              <div className={cn('px-5 py-3 border-t text-sm', colors.border, colors.textSecondary)}>Preparing import…</div>
            )}

            <div className={cn('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-5 py-4 border-t', colors.border, colors.surfaceHover)}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <DocumentTextIcon className="w-5 h-5 shrink-0 text-indigo-600/80 dark:text-indigo-400/90" aria-hidden />
                <div className="min-w-0">
                  {file ? (
                    <>
                      <p className={cn('text-sm font-medium truncate', colors.text)} title={file.name}>
                        {file.name}
                      </p>
                      <p className={cn('text-xs', colors.textMuted)}>
                        {formatBytes(file.size)}
                      </p>
                    </>
                  ) : (
                    <p className={cn('text-sm', colors.textMuted)}>No file selected yet.</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 justify-end flex-wrap">
                {file && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-no-dropzone-click
                    onClick={(ev) => {
                      ev.stopPropagation();
                      clearFile();
                    }}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5"
                  >
                    <XIcon className="w-4 h-4" aria-hidden />
                    Clear
                  </Button>
                )}
                <Button
                  type="submit"
                  data-no-dropzone-click
                  loading={busy}
                  disabled={busy || !file}
                  className="inline-flex items-center justify-center gap-2 px-6 rounded-xl shadow-sm"
                >
                  <UploadCloudIcon className="w-4 h-4 shrink-0" aria-hidden />
                  {busy ? 'Importing…' : 'Run import'}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </form>

      {result && (
        <section className={cn('rounded-2xl border shadow-sm overflow-hidden', colors.border, colors.surface)} aria-labelledby="accountsplan-result-heading">
          <div className={cn('px-5 py-4 border-b flex items-center gap-2', colors.border, colors.surfaceHover)}>
            <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500/25')} aria-hidden>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 id="accountsplan-result-heading" className={cn('text-base font-semibold', colors.text)}>
              Last import result
            </h2>
          </div>
          <div className="px-5 py-4">
            <ul className={cn('grid gap-3 sm:grid-cols-2 text-sm', colors.textSecondary)}>
              <li className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Rows read</span>
                <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{result.totalRows}</p>
              </li>
              <li className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Created</span>
                <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{result.created}</p>
              </li>
              <li className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Updated</span>
                <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{result.updated}</p>
              </li>
              <li className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Parents linked</span>
                <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{result.linkedParents}</p>
              </li>
              <li className={cn('rounded-xl border px-4 py-3', colors.border, colors.surfaceHover)}>
                <span className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>Missing parents</span>
                <p className={cn('mt-1 text-lg font-semibold tabular-nums', colors.text)}>{result.missingParents}</p>
              </li>
            </ul>
          </div>
        </section>
      )}

      <p className={cn('text-xs flex flex-wrap items-start gap-2 max-w-3xl', colors.textMuted)}>
        <InfoIcon className="w-4 h-4 shrink-0 mt-0.5 opacity-70" aria-hidden />
        <span>
          <strong className={cn('font-medium', colors.textSecondary)}>Tip:</strong> Fill level codes left to right (e.g. 00 → 00.00 → 00.00.000 → 00.00.000.6100). Legacy files with dotted <code className="text-xs">Code</code> + <code className="text-xs">Account</code> columns are still supported.
        </span>
      </p>
    </div>
  );
};

export default AccountsPlanImportPage;

